import type { AxiosRequestConfig } from "axios";
import type { SkipTokenRefreshConfig } from "../Api/Plugin/RefreshToken/RefreshTokenResponseInterceptor";
import { executeMutation } from "../Mutation/executeMutation";
import { postMutation } from "../Mutation/hydraMutations";
import { useCrossTabRefresh } from "./crossTabRefresh";
import { useAuthStore } from "./useAuthStore";

// Best effort during logout. A 401 here must not refresh: a logout started by a failed refresh
// would wait on itself and leave the user on the logout screen.
const REVOKE_CURRENT_SESSION_CONFIG = {
  timeout: 5000,
  skipTokenRefresh: true,
} satisfies AxiosRequestConfig & SkipTokenRefreshConfig;

/**
 * Ends the whole session family of the current login (all its refresh tokens) server-side, and
 * drops the rotated markers other tabs of this browser kept for that session.
 * Must run while the access token is still valid: before `revokeAccessToken()` and before the
 * auth store is reset.
 */
export async function revokeCurrentSession(): Promise<void> {
  try {
    if (!useAuthStore().token.value) {
      return;
    }
    await executeMutation(postMutation(), {
      url: "/api/users/me/sessions/current/revoke",
      config: REVOKE_CURRENT_SESSION_CONFIG,
    });
  } catch (error) {
    console.log("Failed to revoke current session", error);
  } finally {
    // The person logs out whether or not the revoke reached the server, so the markers go either way.
    useCrossTabRefresh().forgetSession();
  }
}
